import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Wallet, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCHF = (n) =>
  new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export const NextMonthRevenue = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/dashboard/next-month-revenue`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card data-testid="next-month-revenue-card" className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Wallet className="h-5 w-5 text-emerald-600" />
          Erwarteter Umsatz – {data?.month_label || 'nächster Monat'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Lädt…</div>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-end gap-8">
              <div data-testid="nmr-total">
                <div className="text-3xl font-bold text-emerald-700">{fmtCHF(data?.total)}</div>
                <div className="text-xs text-slate-500">voraussichtlicher Umsatz aus gebuchten Terminen</div>
              </div>
              <div data-testid="nmr-count">
                <div className="text-2xl font-semibold text-slate-800">{data?.count || 0}</div>
                <div className="text-xs text-slate-500">gebuchte Termine</div>
              </div>
            </div>
            {data?.appointments?.length ? (
              <div className="max-h-80 overflow-auto rounded-lg border border-slate-100">
                <table className="w-full text-sm" data-testid="nmr-list">
                  <thead className="sticky top-0 bg-slate-50 text-xs text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Datum</th>
                      <th className="px-3 py-2 text-left font-medium">Kunde</th>
                      <th className="px-3 py-2 text-left font-medium">Leistung</th>
                      <th className="px-3 py-2 text-right font-medium">Betrag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.appointments.map((a, i) => (
                      <tr key={i} className="border-t border-slate-50" data-testid={`nmr-row-${i}`}>
                        <td className="whitespace-nowrap px-3 py-2 text-slate-600">{fmtDateTime(a.start_time)}</td>
                        <td className="px-3 py-2">{a.customer_name}</td>
                        <td className="px-3 py-2 text-slate-600">{a.service_name}</td>
                        <td className="px-3 py-2 text-right font-medium">{fmtCHF(a.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-slate-500" data-testid="nmr-empty">
                Noch keine (direkt zu zahlenden) Termine für {data?.month_label || 'den nächsten Monat'} gebucht.
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default NextMonthRevenue;
